// apps/client/src/pages/ReferralsPage.tsx
//
// Referrals page - V1 cosmic design with V2 architecture
// Features: Share card, referral stats, how-it-works, referral list
//

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useUserStore } from '../store/userStore';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui-kit/Button';
import { GlassCard } from '../ui-kit/GlassCard';
import { BottomNav } from '../ui-kit/Layouts';
import Icon from '../ui-kit/Icon';

interface ReferralsPageProps {
    onNavigate: (view: 'home' | 'game' | 'missions' | 'store' | 'referrals' | 'sunday' | 'profile') => void;
}

export function ReferralsPage({ onNavigate }: ReferralsPageProps) {
    const {
        chips,
        referralCode,
        referralShareUrl,
        referralStats,
        referralLoading,
        fetchReferralData,
    } = useUserStore();
    const { t } = useTranslation();

    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchReferralData();
    }, [fetchReferralData]);

    const handleCopyLink = async () => {
        if (!referralShareUrl) return;
        try {
            await navigator.clipboard.writeText(referralShareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Error copying:', err);
        }
    };

    const handleShare = () => {
        if (!referralShareUrl || !referralCode) return;
        const text = t('referrals.shareText', { code: referralCode });
        const url = `https://t.me/share/url?url=${encodeURIComponent(referralShareUrl)}&text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
    };

    const handleNavAction = (tab: string) => {
        const validViews = ['game', 'store', 'sunday', 'home', 'missions', 'referrals', 'profile'] as const;
        type ValidView = typeof validViews[number];
        if (tab === 'referrals') return; // already here
        if (validViews.includes(tab as ValidView)) {
            onNavigate(tab as ValidView);
        } else {
            onNavigate('home');
        }
    };

    return (
        <div className="min-h-screen bg-purple-gradient flex flex-col">
            {/* Header */}
            <header className="px-4 pt-6 pb-2 flex items-center justify-between">
                <div>
                    <span className="text-[10px] font-bold text-primary uppercase tracking-[0.3em]">BingoTon</span>
                    <h1 className="text-3xl font-display font-black gradient-text-gold">{t('referrals.title')}</h1>
                </div>
                <div className="bg-gold-gradient px-4 py-2 rounded-full flex items-center gap-2 shadow-glow-gold">
                    <Icon name="toll" className="text-lg text-background" />
                    <span className="font-black text-background">{chips.toLocaleString()}</span>
                </div>
            </header>

            <main className="flex-1 px-4 pb-32 space-y-6 overflow-y-auto">
                {referralLoading && (
                    <div className="text-center py-12">
                        <div className="text-4xl animate-bounce mb-4">👥</div>
                        <p className="text-white/40">{t('referrals.loading')}</p>
                    </div>
                )}

                {!referralLoading && (
                    <>
                        {/* Share Card */}
                        <motion.section
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <GlassCard variant="glow" className="p-6 border-primary/20">
                                <div className="text-center mb-6">
                                    <div className="text-5xl mb-3">🎁</div>
                                    <h2 className="font-display font-black text-xl mb-2 text-white">{t('referrals.inviteAndEarn')}</h2>
                                    <p className="text-white/50 text-sm">
                                        {t('referrals.earnAmount')} <span className="text-primary font-bold"></span> {t('referrals.earnPerFriend')}
                                    </p>
                                </div>

                                {/* Code Box */}
                                <div className="bg-surface/50 rounded-2xl p-4 mb-4 border border-white/5">
                                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2">{t('referrals.yourCode')}</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-2xl font-black text-primary tracking-wider">
                                            {referralCode ?? '---'}
                                        </span>
                                        <button
                                            onClick={handleCopyLink}
                                            className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                                        >
                                            <Icon name={copied ? 'check' : 'content_copy'} className="text-white" />
                                        </button>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="grid grid-cols-2 gap-3">
                                    <Button
                                        onClick={handleCopyLink}
                                        variant="secondary"
                                        icon="link"
                                    >
                                        {copied ? t('referrals.copied') : t('referrals.copy')}
                                    </Button>
                                    <Button
                                        onClick={handleShare}
                                        icon="share"
                                    >
                                        {t('referrals.share')}
                                    </Button>
                                </div>
                            </GlassCard>
                        </motion.section>

                        {/* Stats */}
                        <motion.section
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <GlassCard variant="strong" className="p-6">
                                <h3 className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-4">
                                    {t('referrals.yourStats')}
                                </h3>
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                        <div className="text-3xl font-black text-white">{referralStats?.totalReferrals ?? 0}</div>
                                        <div className="text-[10px] text-white/40 uppercase">{t('referrals.invited')}</div>
                                    </div>
                                    <div>
                                        <div className="text-3xl font-black text-success">{referralStats?.completedReferrals ?? 0}</div>
                                        <div className="text-[10px] text-white/40 uppercase">{t('referrals.active')}</div>
                                    </div>
                                    <div>
                                        <div className="text-3xl font-black text-primary">{referralStats?.totalEarned ?? 0}</div>
                                        <div className="text-[10px] text-white/40 uppercase">{t('referrals.earned')}</div>
                                    </div>
                                </div>
                            </GlassCard>
                        </motion.section>

                        {/* How it works */}
                        <motion.section
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <GlassCard className="p-6">
                                <h3 className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-4">
                                    {t('referrals.howItWorks')}
                                </h3>
                                <div className="space-y-4">
                                    {[
                                        { step: 1, icon: 'share', text: t('referrals.step1') },
                                        { step: 2, icon: 'person_add', text: t('referrals.step2') },
                                        { step: 3, icon: 'sports_esports', text: t('referrals.step3') },
                                        { step: 4, icon: 'redeem', text: t('referrals.step4') },
                                    ].map((item) => (
                                        <div key={item.step} className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
                                                <Icon name={item.icon} className="text-primary" />
                                            </div>
                                            <p className="text-white/70 text-sm">{item.text}</p>
                                        </div>
                                    ))}
                                </div>
                            </GlassCard>
                        </motion.section>

                        {/* Referral List */}
                        {referralStats && referralStats.referrals.length > 0 && (
                            <motion.section
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <GlassCard variant="strong" className="p-6">
                                    <h3 className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-4">
                                        {t('referrals.yourReferrals')}
                                    </h3>
                                    <div className="space-y-3">
                                        {referralStats.referrals.map((ref) => (
                                            <div
                                                key={ref.id}
                                                className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-surface flex items-center justify-center border border-white/10">
                                                        <span className="text-sm font-bold text-white">
                                                            {ref.referredUsername?.charAt(0).toUpperCase() ?? '?'}
                                                        </span>
                                                    </div>
                                                    <span className="text-white/70 text-sm">
                                                        @{ref.referredUsername ?? t('referrals.anonymous')}
                                                    </span>
                                                </div>
                                                <span className={`text-xs font-bold ${ref.status === 'REWARDED' ? 'text-success' :
                                                    ref.status === 'COMPLETED' ? 'text-primary' :
                                                        'text-white/30'
                                                    }`}>
                                                    {ref.status === 'REWARDED' ? t('referrals.statusRewarded', { reward: ref.reward }) :
                                                        ref.status === 'COMPLETED' ? t('referrals.statusActive') : t('referrals.statusPending')}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </GlassCard>
                            </motion.section>
                        )}
                    </>
                )}
            </main>

            <BottomNav activeTab="home" onTabChange={handleNavAction} />
        </div >
    );
}
